import { Component, Prop, Host, h, Element, getAssetPath, State, Watch } from '@stencil/core';
import { IconColor, IconType } from './icon-interface';
import { SECONDARY_ICON_NAMES, SOLID_ICON_NAMES } from './icon-names';

const o2IconContent = new Map<string, string>();
const requests = new Map<string, Promise<any>>();

@Component({
  tag: 'o2-icon',
  assetsDirs: ['svg'],
  styleUrl: 'icon.scss',
})
export class O2Icon {
  @Element() private el!: HTMLO2IconElement;

  @State() private svgContent?: string;

  /**
   * The color of the icon.
   */
  @Prop({ reflect: true }) color?: IconColor;

  /**
   * The name of the icon from the svg icon list.
   */
  @Prop({ reflect: true }) name?: string;

  /**
   * Import your own svg icon to component. When custom icon is needed, use src attribute instead of name attribute
   */
  @Prop({ reflect: true }) src?: string;

  /**
   * Defines the icon UI type. Default is "secondary".
   * @deprecated Use "solid" prop instead
   */
  @Prop({ reflect: true }) type?: IconType;

  /**
   * Defines the icon UI type. If solid property is not used, the default icon type "secondary" is rendered.
   */
  @Prop({ reflect: true }) solid?: boolean = false;

  /**
   * Defines size of icon. Default is 1.25em, so by default icon will be as large as defined parent font-size.
   */
  @Prop({ reflect: true }) size?: number;

  /**
   * Defines clicable safe area around icon. Default is 0.
   * Usage: if you want to have icon with 20px size and clicable area 4px, set safeArea to 4 and size to 28 (28px-4px padding = 20px icon size).
   */
  @Prop() safeArea?: number = 0;

  @Watch('name')
  @Watch('src')
  @Watch('type')
  @Watch('solid')
  loadSvgIcon() {
    if ((this.name === undefined || this.name === null) && (this.src === undefined || this.src === null)) {
      console.error('[o2-icon]: name or src attribute must be provided');
      return;
    }

    // check if name for icon type solid exists
    const iconType = this.type === 'solid' || this.solid ? 'solid' : 'secondary';

    if (typeof this.src === 'undefined') {

      if (iconType === 'solid' && !this.isSolidIcon(this.name)) {

        console.error(`[o2-icon]: ${this.name} doesn't exists in solid icon variant`);
        this.svgContent = '';
        return;
      }

      if (iconType === 'secondary' && !this.isSecondaryIcon(this.name)) {
        console.error(`[o2-icon]: ${this.name} doesn't exists in secondary icon variant`);
        this.svgContent = '';
        return;
      }
    }

    if (this.src === '') {
      console.error(`[o2-icon]: In case of usage src attribute instead of name attribute, the src could not be empty.`);
      this.svgContent = '';
      return;
    }

    const url = getAssetPath(`${this.src ? this.src : `./svg/${iconType}/${this.name}.svg`}`);

    if (o2IconContent.has(url)) {
      this.svgContent = o2IconContent.get(url);
    } else {
      this.getSvg(url).then(() => {
        this.svgContent = o2IconContent.get(url);
      });
    }
  }

  @Watch('svgContent')
  watchSvgContentHandler(newValue) {
    if (newValue !== undefined) {
      this.setSvgContent(this.svgContent);
    }
  }

  private setSvgContent(svg: string) {
    this.el.innerHTML = svg;
  }

  private getSvg(url: string) {
    let req = requests.get(url);

    if (!req) {
      if (typeof fetch !== 'undefined') {
        req = fetch(url, {
          method: 'GET',
        }).then(async (response: Response) => {
          if (response.ok) {
            return response.text().then(content => {
              o2IconContent.set(url, content || '');
            });
          } else {
            o2IconContent.set(url, '');
          }
        });

        // cache same requests
        requests.set(url, req);
      } else {
        o2IconContent.set(url, '');
        return Promise.resolve();
      }
    }

    return req;
  }

  private getIconSizeInUnits(size: number): string {
    let iconSize = '1.25em';

    if (size) {
      iconSize = `${size}px`;
    }

    return iconSize;
  }

  private setSvgSize(size: number) {
    const svg = this.el.querySelector('svg');
    if (svg !== null) {
      if (svg.getAttribute('width') === null) svg.setAttribute('width', '100%');
      if (svg.getAttribute('height') === null) svg.setAttribute('height', `${size ? `${size}px` : '100%'}`);
    }
  }

  private setIconSize(size: number) {
    this.el.style.minWidth = this.getIconSizeInUnits(size);
    this.el.style.height = this.getIconSizeInUnits(size);
  }

  private setSafeArea(size: number) {
    const svg = this.el.querySelector('svg');

    if (svg) {
      svg.style.padding = `${size}px`;
    }
  }

  // Method to check if an icon exists in solid folder
  private isSolidIcon(iconName: string): boolean {
    return SOLID_ICON_NAMES.includes(iconName);
  }

  // Method to check if an icon exists in secondary folder
  private isSecondaryIcon(iconName: string): boolean {
    return SECONDARY_ICON_NAMES.includes(iconName);
  }

  connectedCallback() {
    this.loadSvgIcon();
  }

  componentDidRender() {
    if (this.size) {
      this.setIconSize(this.size);
    }

    this.setSafeArea(this.safeArea);
    this.setSvgSize(this.size);
  }

  render() {
    const color = this.color ? `o2-icon--${this.color}` : '';

    return <Host class={`o2-icon ${color}`} role="img" alt="Icon"></Host>;
  }
}
